import React, { useEffect, useRef, useState } from "react";
import { options } from "@/utils/constants/constants";
import TableHead from "@/components/tables/TableHead";
import UsersTable from "@/components/tables/UsersTable";
import { fetchAllGroups } from "@/services/group.service";
import { fetchAllRoles } from "@/services/role.service";
import { fetchUserAsAdmin } from "@/services/user.service";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import { useWindowInfiniteScroll } from "@/utils/hooks/useWindowInfiniteScroll";
import {
    getListCacheBootstrap,
    writeListCache,
} from "@/utils/listPageCache";

const isEmptyFilter = (value: Record<string, unknown>) =>
    Object.keys(value).length === 0;

const AdminList = () => {
    const [
        pageNo,
        setPageNo,
        dataLength,
        setDataLength,
        sorting,
        setSorting,
        filter,
        setFilter,
        searchItem,
        setSearchItem,
        resetDataQueries,
    ] = useDataQueries();

    const [appliedFilter, setAppliedFilter] = useState(filter);

    const listBootstrap = getListCacheBootstrap("admins", {
        dataLength,
        sorting,
        searchItem,
        filter: appliedFilter,
    });

    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [users, setUsers] = useState<any>(listBootstrap.items);
    const [usersLeft, setUsersLeft] = useState(true);
    const [usersHistory, setUsersHistory] = useState<any>(listBootstrap.items);
    const [allFetched, setAllFetched] = useState(false);
    const [dataload, setDataload] = useState(!listBootstrap.hasCache);
    const [groups, setGroups] = useState([]);
    const [roles, setRoles] = useState([]);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);
    const loadMoreLockRef = useRef(false);

    useEffect(() => {
        fetchAllRoles(
            (response: any) => setRoles(response.data.roles),
            (error: any) => console.log(error)
        );
        fetchAllGroups(
            (response: any) => setGroups(response.data.groups),
            (error: any) => console.log(error)
        );
    }, []);

    useEffect(() => {
        const { cacheKey, cached } = getListCacheBootstrap("admins", {
            dataLength,
            sorting,
            searchItem,
            filter: appliedFilter,
        });

        setPageNo(1);
        setAllFetched(false);
        setUsersLeft(true);

        if (cached && isEmptyFilter(appliedFilter)) {
            setUsers(cached.items);
            setUsersHistory(cached.items);
            setTotalCount(cached.totalCount);
            setDataload(false);
            setIsLoading(false);
            return;
        }

        setUsers([]);
        setUsersHistory([]);
        setIsLoading(true);
        setDataload(true);

        fetchUserAsAdmin(
            1,
            dataLength,
            sorting,
            appliedFilter,
            searchItem,
            (response: any) => {
                const nextUsers = response.data.users ?? [];

                writeListCache(
                    cacheKey,
                    nextUsers,
                    response.data.total_count
                );

                setDataload(false);
                setIsLoading(false);
                setTotalCount(response.data.total_count);
                loadMoreLockRef.current = false;

                if (nextUsers.length < dataLength) {
                    setUsersLeft(false);
                    setAllFetched(true);
                } else {
                    setUsersLeft(true);
                    setAllFetched(false);
                }

                setUsers(nextUsers);
                setUsersHistory(nextUsers);
            },
            (error: any) => {
                console.log(error);
                setDataload(false);
                setIsLoading(false);
                loadMoreLockRef.current = false;
            }
        );
    }, [dataLength, sorting, searchItem, appliedFilter]);

    useWindowInfiniteScroll({
        enabled: !allFetched && !isLoading && usersLeft,
        onLoadMore: () => {
            if (loadMoreLockRef.current) {
                return;
            }
            loadMoreLockRef.current = true;

            const nextPage = Number(pageNo) + 1;
            setPageNo(nextPage);
            setIsLoading(true);

            fetchUserAsAdmin(
                nextPage,
                dataLength,
                sorting,
                appliedFilter,
                searchItem,
                (response: any) => {
                    const nextUsers = response.data.users ?? [];
                    const reportedTotal = response.data.total_count;

                    setIsLoading(false);
                    loadMoreLockRef.current = false;
                    setTotalCount(reportedTotal);

                    setUsers((previousUsers: any[]) => {
                        const updatedUsers = [...previousUsers, ...nextUsers];
                        const hasMore =
                            nextUsers.length > 0 &&
                            (reportedTotal != null
                                ? updatedUsers.length < reportedTotal
                                : nextUsers.length >= dataLength);
                        setUsersLeft(hasMore);
                        setAllFetched(!hasMore);
                        setUsersHistory(updatedUsers);
                        return updatedUsers;
                    });
                },
                (error: any) => {
                    console.log(error);
                    setIsLoading(false);
                    loadMoreLockRef.current = false;
                }
            );
        },
    });

    const reset = () => {
        resetDataQueries?.();
        setAppliedFilter({});
        setPageNo(1);
        setUsersLeft(true);
        setAllFetched(false);
        setUsers([]);
        setUsersHistory([]);
        setDataload(true);
        setIsLoading(true);
    };

    const search = (searchText: string) => {
        setSearchItem(searchText);
    };

    const handleApplyFilter = (filterOverride?: any) => {
        const nextFilter = filterOverride ?? filter;

        setPageNo(1);
        setUsersLeft(true);
        setAllFetched(false);
        setUsers([]);
        setUsersHistory([]);
        setDataload(true);
        setIsLoading(true);
        setAppliedFilter(nextFilter);
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id="admin-table"
                    handleReset={reset}
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    roles={roles}
                    groups={groups}
                    setFilter={setFilter}
                    filter={filter}
                    setDataLength={
                        setDataLength as (dataLength: number) => void
                    }
                    reset={reset}
                    search={search}
                    title="Admins"
                    handleApplyFilter={handleApplyFilter}
                    options={options}
                    currentEnvo={users.length}
                    totalEnvo={totalCount}
                    searchItem={searchItem as string}
                />
            </div>

            <div>
                <UsersTable
                    is_allow_manage_shared_access={false}
                    id="admin-table"
                    reset={reset}
                    title="Admins"
                    setUsers={setUsers}
                    search={search}
                    isLoading={isLoading}
                    dataLength={dataLength as number}
                    sorting={sorting}
                    setSorting={setSorting as (sorting: any) => void}
                    searchItem={searchItem as string}
                    allFetched={allFetched}
                    users={users}
                    setPageNo={setPageNo}
                    dataload={dataload}
                    resetDataQueries={resetDataQueries}
                />
            </div>
        </div>
    );
};

export default AdminList;
